import {
	getVaultDepositorBalance,
	getVaultDepositorNotionalNetDeposits,
} from 'src/utils/vault/math';
import {
	BigNum,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
} from '@velocity-exchange/sdk';
import { useVaultOnChainData } from 'src/hooks/query/vaults/base/useVaultOnChainData';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';
import { useVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { MarketId } from '@velocity-exchange/common';
import { UiVaults } from 'src/constants/vaults';
import { QUOTE_ASSET_SYMBOL } from 'src/constants/constants';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { useVaultDepositorHistories } from 'src/hooks/query/vaults/base/useVaultDepositorHistories';
import { calcModifiedDietz } from '@velocity-exchange/vaults-sdk';
import { formatText } from '@velocity-exchange/common/format';
import { PRESETS } from 'src/utils/formatNumber/presets';
import ValueDisplay from 'src/components/ValueDisplay';
import SkeletonValuePlaceholder from 'src/components/SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import { VaultPerformanceStatLabel } from '../VaultPerformanceStatLabel';
import Divider from 'src/components/primitives/Divider/Divider';

export const VaultDepositorStatsForSidebar = (props: {
	depositAssetConfig: SpotMarketConfig;
	vaultPubkey: string;
}) => {
	const getOraclePrice = useGetOraclePriceForMarket();
	const { data: vaultStats } = useVaultStats(props.vaultPubkey);
	const vaultTvlBase = vaultStats?.tvlBase;
	const marketId = MarketId.createSpotMarket(
		props.depositAssetConfig.marketIndex
	);
	const uiVaultConfig = UiVaults.getVaultConfig(props.vaultPubkey);
	const { data: historiesLookup, isLoading: isVaultDepositorHistoryLoading } =
		useVaultDepositorHistories();
	const vaultDepositorHistory = historiesLookup?.[props.vaultPubkey] ?? [];
	const { data: onChainData } = useVaultOnChainData();
	const { data: depositorAccounts, isLoading: isDepositorLoading } =
		useVaultDepositorAccounts();
	const vaultAccountData = onChainData?.[props.vaultPubkey]?.vaultAccountData;
	const vaultDepositorAccountData =
		depositorAccounts?.[props.vaultPubkey]?.vaultDepositor;
	const isVaultDepositorLoaded = !isDepositorLoading;

	const currentUserBaseBalance = getVaultDepositorBalance(
		vaultDepositorAccountData,
		vaultAccountData,
		vaultTvlBase,
		props.depositAssetConfig.precisionExp,
		true
	);
	const currentUserNotionalBalance = getCurrentUserNotionalBalance();

	const totalBaseEarnings = getTotalBaseEarnings();
	const totalNotionalEarnings = getTotalNotionalEarnings();

	const roi = getTimeWeightedRoi();

	if (!vaultDepositorAccountData || !uiVaultConfig) return null;

	function getCurrentUserNotionalBalance() {
		const oraclePrice = getOraclePrice(marketId);
		const calculatedNotionalBalance = currentUserBaseBalance
			.mul(oraclePrice ?? BigNum.from(0, QUOTE_PRECISION_EXP))
			.shiftTo(QUOTE_PRECISION_EXP);

		return calculatedNotionalBalance;
	}

	function getTotalBaseEarnings() {
		if (!vaultDepositorAccountData || !vaultAccountData)
			return BigNum.from(0, props.depositAssetConfig.precisionExp);

		const totalEarnings = currentUserBaseBalance.sub(
			BigNum.from(
				vaultDepositorAccountData.netDeposits,
				props.depositAssetConfig.precisionExp
			)
		);

		return totalEarnings;
	}

	function getTotalNotionalEarnings() {
		const userNotionalNetDeposits = getVaultDepositorNotionalNetDeposits(
			vaultDepositorHistory ?? []
		);

		const calculatedEarnings = currentUserNotionalBalance.sub(
			userNotionalNetDeposits
		);

		return calculatedEarnings;
	}

	function getTimeWeightedRoi() {
		if (
			!vaultDepositorAccountData ||
			vaultDepositorAccountData.totalDeposits.eqn(0) ||
			!vaultAccountData
		)
			return 0;

		const formattedHistory = vaultDepositorHistory
			?.filter((r) => r.action === 'deposit' || r.action === 'withdraw')
			?.map((deposit) => {
				return {
					ts: deposit.ts,
					marketIndex: deposit.spotMarketIndex, // not needed
					amount: uiVaultConfig?.isNotionalGrowthStrategy
						? deposit.notionalValue
						: deposit.amount,
					direction: deposit.action as 'deposit' | 'withdraw',
				};
			})
			.sort((a, b) => +b.ts - +a.ts); // need to sort in descending order

		if (uiVaultConfig?.isNotionalGrowthStrategy) {
			const { returns } = calcModifiedDietz(
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				currentUserNotionalBalance as any,
				QUOTE_PRECISION_EXP,
				formattedHistory ?? []
			);

			return returns * 100;
		} else {
			const { returns } = calcModifiedDietz(
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				currentUserBaseBalance as any,
				props.depositAssetConfig.precisionExp,
				formattedHistory ?? []
			);

			return returns * 100;
		}
	}

	const stats = [
		{
			label: 'Your Balance',
			value: (
				<span className="flex items-center gap-xs">
					<MarketIcon
						marketSymbol={props.depositAssetConfig.symbol}
						className="size-lg"
					/>
					{uiVaultConfig.isNotionalGrowthStrategy
						? currentUserNotionalBalance.prettyPrint()
						: currentUserBaseBalance.prettyPrint()}
				</span>
			),
			marketSymbol: uiVaultConfig.isNotionalGrowthStrategy
				? QUOTE_ASSET_SYMBOL
				: props.depositAssetConfig.symbol,
			loading:
				(vaultDepositorAccountData && !isVaultDepositorLoaded) ||
				(uiVaultConfig.isNotionalGrowthStrategy &&
					isVaultDepositorHistoryLoading),
			tooltip:
				'The amount of deposit asset that you currently have in the vault, after fees.',
			subValue: uiVaultConfig.isNotionalGrowthStrategy ? (
				<span className="flex items-center gap-xs typo-body-xs">
					<MarketIcon
						marketSymbol={props.depositAssetConfig.symbol}
						className="size-lg"
					/>
					<span>{currentUserBaseBalance.prettyPrint()}</span>
				</span>
			) : undefined,
		},
		...(!uiVaultConfig.isNotionalGrowthStrategy
			? [
					{
						label: 'Notional Balance',
						value: formatText(currentUserNotionalBalance, PRESETS.usd),
						loading: vaultDepositorAccountData && !isVaultDepositorLoaded,
					},
				]
			: []),
		{
			label: 'Your Earnings (All Time)',
			value: (
				<span className="flex items-center gap-xs">
					<MarketIcon
						marketSymbol={props.depositAssetConfig.symbol}
						className="size-lg"
					/>
					{uiVaultConfig.isNotionalGrowthStrategy
						? totalNotionalEarnings.prettyPrint()
						: totalBaseEarnings.prettyPrint()}
				</span>
			),
			marketSymbol: uiVaultConfig.isNotionalGrowthStrategy
				? QUOTE_ASSET_SYMBOL
				: props.depositAssetConfig.symbol,
			loading: vaultDepositorAccountData && !isVaultDepositorLoaded,
			tooltip: 'Your total earnings from the vault, after fees.',
			subValue: uiVaultConfig.isNotionalGrowthStrategy ? (
				<span className="flex items-center gap-xs typo-body-xs">
					<MarketIcon
						marketSymbol={props.depositAssetConfig.symbol}
						className="size-lg"
					/>
					<span>{totalBaseEarnings.prettyPrint()}</span>
				</span>
			) : undefined,
		},
		...(!uiVaultConfig.isNotionalGrowthStrategy
			? [
					{
						label: 'Notional Earnings',
						value: formatText(
							totalBaseEarnings
								.mul(getOraclePrice(marketId))
								.shiftTo(QUOTE_PRECISION_EXP),
							PRESETS.usd
						),
						marketSymbol: uiVaultConfig.isNotionalGrowthStrategy
							? QUOTE_ASSET_SYMBOL
							: props.depositAssetConfig.symbol,
						loading: vaultDepositorAccountData && !isVaultDepositorLoaded,
					},
				]
			: []),
		{
			label: 'ROI',
			value: `${roi.toFixed(2)}%`,
			loading: vaultDepositorAccountData && !isVaultDepositorLoaded,
		},
	];

	return (
		<>
			<div className="flex w-full flex-col gap-y-lg">
				<span className="typo-label-sm text-text-default">Your Deposit</span>
				<div className="flex flex-col gap-y-sm">
					{stats.map((stat) => {
						return (
							<ValueDisplay.Default
								key={stat.label}
								label={
									<VaultPerformanceStatLabel
										label={stat.label}
										tooltip={stat.tooltip}
									/>
								}
								value={
									stat.loading ? (
										<SkeletonValuePlaceholder className="h-2xl w-20" loading />
									) : (
										stat.value
									)
								}
							/>
						);
					})}
				</div>
			</div>
			<Divider />
		</>
	);
};
